import { createSlice, createEntityAdapter, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { AddressWithErrors } from "../../../models/AwarenessCampaign";
import { LoadableContext, RootState } from "../../store";

const addressesAdapter = createEntityAdapter<AddressWithErrors>({
    selectId: (address) => address.ID,
});

type AddressesState = ReturnType<typeof addressesAdapter.getInitialState> & LoadableContext & {
    awarenessCampaignID: string | null;
    sorted: boolean;
};

const initialState: AddressesState = addressesAdapter.getInitialState({
    loading: false,
    error: null,
    awarenessCampaignID: null,
    sorted: false,
});

const addressesSlice = createSlice({
    name: 'campaign_addresses',
    initialState,
    reducers: {
        setAwarenessCampaignID: (state, action: PayloadAction<string>) => {
            state.awarenessCampaignID = action.payload;
            addressesAdapter.removeAll(state);
        },
        setAddresses: (state, action: PayloadAction<AddressWithErrors[]>) => {
            const visitedAddressesOnTop = [
                ...action.payload.filter(address => !address.visited),
                ...action.payload.filter(address => address.visited)
            ];
            addressesAdapter.setAll(state, visitedAddressesOnTop);
            state.sorted = false;
            state.loading = false;
            state.error = null;
        },
        addAddresses: (state, action: PayloadAction<AddressWithErrors[]>) => {
            addressesAdapter.addMany(state, action.payload);
            state.loading = false;
            state.error = null;
        },
        updateMany: (state, { payload: updates }: PayloadAction<{ id: string; changes: Partial<AddressWithErrors> }[]>) => {
            addressesAdapter.updateMany(state, updates);
            state.loading = false;
        },
        startLoading: (state) => {
            state.loading = true;
            state.error = null;
        },
        setSortedAddresses: (state, action: PayloadAction<AddressWithErrors[]>) => {
            addressesAdapter.setAll(state, action.payload);
            state.sorted = true;
            state.loading = false;
            state.error = null;
        },
        stopLoading: (state) => {
            state.loading = false;
            state.error = null;
        },
        setError: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        clearAddresses: (state) => {
            addressesAdapter.removeAll(state);
            state.awarenessCampaignID = null;
            state.loading = false;
            state.error = null;
        },
    },
});

export const CampaignAddressesActions = addressesSlice.actions;

export const {
    selectAll: selectAllCampaignAddresses,
    selectById: selectCampaignAddressById,
    selectIds: selectCampaignAddressesIds
} = addressesAdapter.getSelectors((state: RootState) => state.awareness_campaign.addresses);

// Memoized selectors for filtered addresses
export const selectNonVisitedAddresses = createSelector(
    [selectAllCampaignAddresses],
    (addresses) => addresses.filter(address => !address.visited)
);

export const selectVisitedAddresses = createSelector(
    [selectAllCampaignAddresses],
    (addresses) => addresses.filter(address => address.visited)
);


const CampaignAddressesReducer = addressesSlice.reducer

export default CampaignAddressesReducer;
